import React, { useState, useEffect } from 'react';
import { Row, Col, Card, Statistic, Table, Tag, Spin, Alert, List, Badge, Input } from 'antd';
import { ArrowUpOutlined, ArrowDownOutlined, AccountBookOutlined, StockOutlined, RiseOutlined, FallOutlined, LaptopOutlined, CheckCircleOutlined, MinusCircleOutlined, TeamOutlined } from '@ant-design/icons';
import ReactEChartsCore from 'echarts-for-react';
import { accountApi, tradingApi, clientApi } from '../services/api';

export default function DashboardPage() {
  const [positions, setPositions] = useState([]);
  const [orders, setOrders] = useState([]);
  const [fills, setFills] = useState([]);
  const [clients, setClients] = useState([]);
  const [assetStats, setAssetStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [posCidSearch, setPosCidSearch] = useState('');
  const [orderCidSearch, setOrderCidSearch] = useState('');
  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const clientRes = await clientApi.getOnline();
      if (clientRes.success) setClients(clientRes.data);

      const posRes = await accountApi.getAllPositions();
      if (posRes.success) setPositions(posRes.data);

      // 获取当日委托
      const todayRes = await clientApi.getTodayOrders('');
      if (todayRes.success) setOrders(todayRes.data?.today_orders || []);

      // 资产统计（后端统一计算，只统计在线客户端）
      const statRes = await accountApi.getAssetStats();
      if (statRes.success) setAssetStats(statRes.data || {});

    } catch (err) {
      console.error('加载数据失败:', err);
    } finally {
      setLoading(false);
    }
  };

  const onlineClients = clients.filter(c => c.status === 'online');
  const onlineClientIds = new Set(onlineClients.map(c => c.id));
  const onlinePositions = positions.filter(p => onlineClientIds.has(p.client_id));
  // 资产统计统一使用后端 /stats/asset 返回（只统计在线客户端）
  const totalAssets = assetStats.total_asset || 0;
  const totalBalance = assetStats.total_available || 0;
  const totalMarketValue = assetStats.total_market_value || 0;
  const totalPL = assetStats.total_pl || 0;

  const assetOption = {
    tooltip: { trigger: 'item', formatter: '{b}: {c}元 ({d}%)' },
    series: [{
      type: 'pie', radius: ['40%', '70%'],
      center: ['50%', '55%'],
      data: [
        { value: Math.round(totalBalance), name: '可用资金', itemStyle: { color: '#27ae60' } },
        { value: Math.round(totalMarketValue), name: '持仓市值', itemStyle: { color: '#2980b9' } },
      ],
      label: { show: true, formatter: '{b}\n{c}元' },
      emphasis: { itemStyle: { shadowBlur: 10, shadowOffsetX: 0, shadowColor: 'rgba(0,0,0,0.5)' } }
    }]
  };

  // 有成本价的在线持仓才参与盈亏排行（前端明细展示用）
  const validPositions = onlinePositions.filter(p => p.cost_price && p.cost_price > 0);
  const topPositions = [...validPositions].sort((a, b) => Math.abs(b.profit_loss || 0) - Math.abs(a.profit_loss || 0)).slice(0, 8);
  const barOption = {
    tooltip: { trigger: 'axis', axisPointer: { type: 'shadow' } },
    grid: { left: '3%', right: '4%', bottom: '3%', containLabel: true },
    xAxis: { type: 'category', data: topPositions.map(p => p.stock_name), axisLabel: { rotate: 30 } },
    yAxis: { type: 'value', name: '盈亏(元)' },
    series: [{
      type: 'bar',
      data: topPositions.map(p => ({
        value: Math.round(p.profit_loss || 0),
        itemStyle: { color: (p.profit_loss || 0) >= 0 ? '#e74c3c' : '#27ae60' }
      })),
      barWidth: '50%'
    }]
  };

  const orderColumns = [
    { title: '客户端', dataIndex: 'client_id', width: 70, render: v => <code style={{ fontSize: 11 }}>{v || '-'}</code> },
    { title: '时间', dataIndex: 'order_time_str', key: 'order_time_str', width: 70, render: v => v || '-' },
    { title: '股票', key: 'stock', width: 120, render: (_, r) => `${r.stock_name}(${r.stock_code})` },
    { title: '方向', dataIndex: 'direction', key: 'direction', width: 60, render: v => {
      const isBuy = v && (v.includes('买入') || v === 'buy');
      return <Tag color={isBuy ? 'red' : 'green'}>{isBuy ? '买' : '卖'}</Tag>;
    }},
    { title: '价格', dataIndex: 'price', key: 'price', width: 80, render: v => v ? v.toFixed(2) : '-' },
    { title: '数量', dataIndex: 'quantity', key: 'quantity', width: 70 },
    { title: '状态', dataIndex: 'status', key: 'status', width: 80, render: v => {
      const colors = { pending: 'default', submitted: 'processing', partially_filled: 'warning', filled: 'success', cancelled: 'default', rejected: 'error' };
      const labels = { pending: '待处理', submitted: '已提交', partially_filled: '部分成交', filled: '已成交', cancelled: '已撤销', rejected: '已拒绝' };
      return <Tag color={colors[v] || 'default'}>{labels[v] || v}</Tag>;
    }},
  ];

  if (loading) return <Spin size="large" style={{ display: 'block', margin: '100px auto' }} />;

  return (
    <div>
      {/* 资产统计 */}
      <Row gutter={[16, 16]}>
        <Col span={5}>
          <Card><Statistic title="管理客户" value={onlineClients.length} suffix="户" prefix={<TeamOutlined />} valueStyle={{ color: '#8e44ad' }} /></Card>
        </Col>
        <Col span={5}>
          <Card><Statistic title="总资产" value={totalAssets} precision={2} suffix="元" prefix={<AccountBookOutlined />} valueStyle={{ color: '#2980b9' }} /></Card>
        </Col>
        <Col span={5}>
          <Card><Statistic title="可用资金" value={totalBalance} precision={2} suffix="元" prefix={<RiseOutlined />} valueStyle={{ color: '#27ae60' }} /></Card>
        </Col>
        <Col span={5}>
          <Card><Statistic title="持仓市值" value={totalMarketValue} precision={2} suffix="元" prefix={<StockOutlined />} valueStyle={{ color: '#e67e22' }} /></Card>
        </Col>
        <Col span={4}>
          <Card><Statistic title="持仓盈亏" value={totalPL} precision={2} suffix="元" prefix={totalPL >= 0 ? <ArrowUpOutlined /> : <ArrowDownOutlined />} valueStyle={{ color: totalPL >= 0 ? '#e74c3c' : '#27ae60' }} /></Card>
        </Col>
      </Row>

      {/* 图表 */}
      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={10}>
          <Card title="资产分布" size="small">
            <ReactEChartsCore option={assetOption} style={{ height: 240 }} />
          </Card>
        </Col>
        <Col span={6}>
          <Card title="持仓资金分布" size="small">
            <ReactEChartsCore option={{
              tooltip: { trigger: 'item', formatter: '{b}: {c}元 ({d}%)' },
              series: [{
                type: 'pie', radius: ['40%', '70%'],
                data: positions.filter(p => (p.market_value || 0) > 0).map(p => ({ value: Math.round(p.market_value || 0), name: p.stock_name || p.stock_code })),
                label: { show: true, formatter: '{b}\n{c}元', fontSize: 10 },
              }]
            }} style={{ height: 240 }} />
          </Card>
        </Col>
        <Col span={8}>
          <Card title="账户资金" size="small">
            <ReactEChartsCore option={{
              tooltip: { trigger: 'axis' },
              grid: { left: 80, right: 20, bottom: 20 },
              xAxis: { type: 'category', data: ['总资产', '可用资金', '持仓市值', '持仓盈亏'] },
              yAxis: { type: 'value', name: '元' },
              series: [{
                type: 'bar', barWidth: '40%',
                data: [
                  { value: Math.round(totalAssets), itemStyle: { color: '#2980b9' } },
                  { value: Math.round(totalBalance), itemStyle: { color: '#27ae60' } },
                  { value: Math.round(totalMarketValue), itemStyle: { color: '#e67e22' } },
                  { value: Math.round(totalPL), itemStyle: { color: totalPL >= 0 ? '#e74c3c' : '#27ae60' } },
                ],
              }]
            }} style={{ height: 240 }} />
          </Card>
        </Col>
      </Row>

      <Row gutter={[16, 16]} style={{ marginTop: 16 }}>
        <Col span={12}>
          <Card title="正在持仓列表" size="small" bodyStyle={{ padding: 0 }}>
            <div style={{ padding: '8px 8px 0' }}><Input.Search placeholder="搜索客户端ID" value={posCidSearch} onChange={e => setPosCidSearch(e.target.value)} allowClear style={{ width: 200 }} /></div>
            <Table dataSource={onlinePositions.filter(p => (p.quantity || 0) > 0 && (!posCidSearch || (p.client_id || '').includes(posCidSearch)))} columns={[
              { title: '客户端', dataIndex: 'client_id', width: 70, render: v => <code style={{ fontSize: 11 }}>{v || '-'}</code> },
              { title: '股票', key: 'stock', width: 110, render: (_, r) => <>{r.stock_name}<br /><small style={{ color: '#999' }}>{r.stock_code}</small></> },
              { title: '数量', dataIndex: 'quantity', width: 60, render: v => v?.toLocaleString() },
              { title: '市价', dataIndex: 'current_price', width: 65, render: v => v?.toFixed(2) },
              { title: '成本', dataIndex: 'cost_price', width: 65, render: v => v?.toFixed(3) },
              { title: '盈亏', dataIndex: 'profit_loss', width: 85, render: v => (
                <span style={{ color: v >= 0 ? '#e74c3c' : '#27ae60', fontWeight: 600 }}>{v >= 0 ? '+' : ''}{v?.toFixed(2)}</span>
              )},
              { title: '比例', dataIndex: 'profit_loss_ratio', width: 60, render: v => v ? `${v.toFixed(2)}%` : '-' },
              { title: '市值', dataIndex: 'market_value', width: 80, render: v => v?.toLocaleString() },
            ]} rowKey="id" size="small" pagination={false} scroll={{ y: 240 }} />
          </Card>
        </Col>
        <Col span={12}>
          <Card title="今日委托" size="small" bodyStyle={{ padding: 0 }}>
            <div style={{ padding: '8px 8px 0' }}><Input.Search placeholder="搜索客户端ID" value={orderCidSearch} onChange={e => setOrderCidSearch(e.target.value)} allowClear style={{ width: 200 }} /></div>
            <Table
              dataSource={orders.filter(o => onlineClientIds.has(o.client_id) && ((o.status || '').includes('已成') || (o.status || '').includes('已报')) && (!orderCidSearch || (o.client_id || '').includes(orderCidSearch)))}
              columns={orderColumns}
              rowKey="id"
              size="small"
              pagination={false}
              scroll={{ y: 240 }}
            />
          </Card>
        </Col>
      </Row>
    </div>
  );
}
